import React, {useMemo} from 'react';

import {FeaturesEnum} from 'presentation/constants';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {getHealthStepPosition} from 'presentation/modules/booking/health/utils/getHealthStepPosition';
import {FlowEnum} from 'presentation/modules/booking/shared/constants/flowEnum';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {Location} from 'presentation/modules/booking/shared/ui/pages/Location';
import {HealthBookingStepsProps} from './types';
import {HealthSpecialtySelection} from '../HealthSpecialtySelection';
import {HealthDistrictSelection} from '../HealthDistrictSelection';
import {HealthUnits} from '../HealthUnits';
import {HealthProfessionalSelection} from '../HealthProfessionalSelection';
import {HealthReviewBooking} from '../HealthReviewBooking';
import {HealthReviewBookingNew} from '../HealthReviewBookingNew';

export function HealthStepsWrapper({
	activeStep,
	handleBack,
	handleNext,
	stepLabels,
}: HealthBookingStepsProps) {
	const bookingData = useBookingStore(state => state.bookingData);
	const {isManualLocation} = bookingData;
	const FLOW_TYPE = FlowEnum.HEALTH;
	const {isEnabled: isNewResumePageEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_NEW_RESUME_BOOKING_PAGE,
	);

	const stepPositions = useMemo(
		() => getHealthStepPosition({isManualLocation}),
		[isManualLocation],
	);

	const {
		LOCATION_STEP,
		SPECIALITY_OR_EXAM_STEP,
		DISTRICT_STEP,
		UNIT_STEP,
		PROFESSIONAL_STEP,
		REVIEW_STEP,
	} = stepPositions;

	const stepContent = useMemo(() => {
		const baseSteps: Record<number, JSX.Element | null> = {
			[LOCATION_STEP]: <Location flowType={FLOW_TYPE} />,
			[SPECIALITY_OR_EXAM_STEP]: <HealthSpecialtySelection handleNextStep={handleNext} />,
			[UNIT_STEP]: <HealthUnits handleNextStep={handleNext} />,
			[PROFESSIONAL_STEP]: <HealthProfessionalSelection handleNextStep={handleNext} />,
			[REVIEW_STEP]: isNewResumePageEnabled ? (
				<HealthReviewBookingNew handlePrevStep={handleBack} />
			) : (
				<HealthReviewBooking stepLabels={stepLabels} handlePrevStep={handleBack} />
			),
		};

		if (isManualLocation) {
			baseSteps[DISTRICT_STEP] = <HealthDistrictSelection handleNextStep={handleNext} />;
		}

		return baseSteps;
	}, [
		LOCATION_STEP,
		SPECIALITY_OR_EXAM_STEP,
		DISTRICT_STEP,
		UNIT_STEP,
		PROFESSIONAL_STEP,
		REVIEW_STEP,
		isManualLocation,
		isNewResumePageEnabled,
		handleNext,
		handleBack,
		activeStep,
		stepLabels,
	]);

	return stepContent[activeStep] ?? null;
}
